(function () {
  "use strict";

  /* Registration lookup - resolve the car to an actual product page.
   *
   * WHY THIS EXISTS. The worker at evch-reg-lookup returns a links array, and
   * for most makes those links are collections rather than products. The
   * finder log for CK23GMO (2023 Volvo XC40 Ultimate Recharge) recorded
   * "no product page for this model, sent to a collection", and the same held
   * for every make the worker has no model mapping for. The snippet's own help
   * text promises to "take you to the exact variant", so a collection landing
   * is a broken promise as well as a lost sale.
   *
   * Rather than add a second model -> handle table that needs maintaining
   * every time a product is published, this asks the store what matches the
   * car. The mapping then maintains itself: publish a model page and the
   * finder starts using it, with no code change here.
   *
   * TWO SOURCES, IN THIS ORDER, AND THE ORDER MATTERS.
   *
   *   1. The brand collection the worker already links to, read as
   *      /collections/<handle>/products.json. This is live data - a product
   *      appears the moment it is published.
   *   2. Shopify predictive search, as a fallback for cars whose brand
   *      collection is missing, unpublished or does not contain the product.
   *
   * Search alone was the first design and it was wrong. Shopify reindexes
   * asynchronously after a publish, and measured on the twelve model pages
   * published on 2026-09-04, multi-term search still could not find ten of
   * them an hour later, while the collection JSON had all twelve instantly.
   * A finder that goes blind for several hours after every product launch is
   * a finder that fails exactly when it is needed most.
   *
   * Separate file, MutationObserver on the result region, same pattern and for
   * the same reason as reg-lookup-length.js - the worker contract in
   * reg-lookup.js stays untouched, and reg-lookup-length.js then applies the
   * customer's chosen cable length to the product link this adds.
   *
   * THE GUARD IS THE POINT. Predictive search always returns its best effort,
   * and its best effort is often the wrong car. A near-miss is worse than the
   * collection fallback, because it tells someone we have a page for their car
   * when we do not. Verified against the live catalogue, 30 cases, including
   * every trap found while building it:
   *
   *   Volvo XC90 / XC60      -> collection. Nearest product is the XC40; a
   *                             different car.
   *   Hyundai IONIQ 9        -> collection. The unnumbered "Hyundai Ioniq"
   *                             page is the 2016 car, 7.2kW, not the IONIQ 9.
   *   Mercedes EQA 250       -> the EQA page. Here 250 is a trim, not a model.
   *   Tesla Model Y          -> Model Y, not Model 3.
   *   Nissan Leaf Tekna      -> the Leaf page; Tekna is a trim we can ignore.
   *   Cupra Born V2          -> the Born page; V2 is a trim, not a model code.
   */

  if (window.__evchRegProduct) return;
  window.__evchRegProduct = true;

  /* Trim levels and powertrain words. Not model identifiers - leaving them in
   * would let "Recharge" match any Volvo. */
  var NOISE = /^(ev|electric|recharge|ultimate|core|plus|pro|premium|essential|launch|edition|line|long|range|awd|rwd|fwd|phev|hev|mhev|hybrid|auto|automatic|manual|tech|design|dynamic|sport|sports|style|comfort|business|nav|se|st|gt|hp|ps|kwh|kw|d|dr|door|estate|saloon|hatch|suv|coupe|tourer|the)$/;

  function norm(s) {
    return String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  /* Handle split into its parts, so a bare number can be matched as a whole
   * segment. Without this, "250" matches inside "a250e" and an EQA owner is
   * sent to the A250e page. */
  function segs(s) {
    return String(s == null ? "" : s).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }

  function isDigits(t) { return /^[0-9]+$/.test(t); }
  function isCode(t) { return /[a-z]/.test(t) && /[0-9]/.test(t); }
  function isWord(t) { return /^[a-z]+$/.test(t) && t.length >= 3; }
  function distinguishing(t) { return t.length >= 3 || (t.length >= 1 && /[0-9]/.test(t)); }

  function carFrom(region) {
    var el = region.querySelector(".reg-result-car");
    if (!el) return null;
    var clone = el.cloneNode(true);
    var small = clone.querySelector("small");
    if (small && small.parentNode) small.parentNode.removeChild(small);

    var parts = clone.textContent.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
    if (parts.length && /^(19|20)\d{2}$/.test(parts[0])) parts.shift();
    if (parts.length < 2) return null;

    var make = parts.shift();
    var tokens = [], all = [];
    for (var i = 0; i < parts.length; i++) {
      var t = norm(parts[i]);
      if (!t || NOISE.test(t)) continue;
      all.push(t);
      if (distinguishing(t)) tokens.push(t);
    }
    if (!tokens.length) return null;

    var m = norm(make);
    return {
      query: (make + " " + parts.join(" ")).trim(),
      /* Mercedes-Benz normalises to mercedesbenz while its handles read
       * mercedes-..., so match a prefix rather than the whole make. */
      makeKey: m.slice(0, Math.min(5, m.length)),
      tokens: tokens,
      /* Single letters are useless on their own but decisive together with
       * the rest: "Model Y" and "Model 3" differ by one character. Kept for
       * the all-tokens pass only. */
      all: all
    };
  }

  var cache = {};
  function getJSON(url) {
    if (!cache[url]) {
      cache[url] = fetch(url, { headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return cache[url];
  }

  /* Brand collections the worker already put on screen. Filtered to ones
   * naming the make, which both scopes the search correctly and avoids
   * pulling the 185-product type-2-charging-cables listing over the wire. */
  function brandCollections(actions, car) {
    var out = [], seen = {};
    Array.prototype.forEach.call(actions.querySelectorAll('a[href*="/collections/"]'), function (a) {
      var m = /\/collections\/([^/?#]+)/.exec(a.getAttribute("href") || "");
      if (!m) return;
      var h = m[1];
      if (seen[h]) return;
      if (norm(h).indexOf(car.makeKey) === -1) return;
      seen[h] = 1;
      out.push(h);
    });
    return out;
  }

  function fromCollections(handles) {
    return Promise.all(handles.map(function (h) {
      return getJSON("/collections/" + encodeURIComponent(h) + "/products.json?limit=250");
    })).then(function (results) {
      var products = [];
      results.forEach(function (d) {
        if (!d || !d.products) return;
        d.products.forEach(function (p) {
          products.push({ handle: p.handle, title: p.title, url: "/products/" + p.handle });
        });
      });
      return products;
    });
  }

  function fromSearch(q) {
    /* Brackets percent-encoded deliberately - some clients mangle them. */
    return getJSON("/search/suggest.json?q=" + encodeURIComponent(q) +
      "&resources%5Btype%5D=product&resources%5Blimit%5D=8").then(function (j) {
      var ps = (j && j.resources && j.resources.results && j.resources.results.products) || [];
      return ps.map(function (p) {
        return { handle: p.handle, title: p.title, url: "/products/" + p.handle };
      });
    });
  }

  function pick(products, car) {
    var toks = car.tokens, codes = [], nums = [], words = [], i;
    for (i = 0; i < toks.length; i++) {
      if (isCode(toks[i])) codes.push(toks[i]);
      else if (isDigits(toks[i])) nums.push(toks[i]);
      else if (isWord(toks[i])) words.push(toks[i]);
    }

    function usable(p) {
      var h = norm(p.handle || p.url);
      /* A discharge lead is not a charging cable. The V2L path in
       * reg-lookup.js handles those, and checks the car supports V2L first. */
      if (h.indexOf("v2l") > -1 || h.indexOf("discharge") > -1) return null;
      if (h.indexOf(car.makeKey) === -1) return null;
      return h;
    }

    function scan(test) {
      for (var k = 0; k < products.length; k++) {
        var h = usable(products[k]);
        if (h === null) continue;
        if (test(products[k], h)) return products[k];
      }
      return null;
    }

    function contains(h, list) {
      for (var k = 0; k < list.length; k++) if (h.indexOf(list[k]) > -1) return true;
      return false;
    }

    /* P1 - every token present, including the single letters. This is what
     * separates Model Y from Model 3, and it is why single letters are matched
     * as whole handle segments: "s" occurs inside "tesla", so Model S would
     * otherwise match every Tesla page. */
    var all = car.all || toks;
    var hit = scan(function (p, h) {
      var s = segs(p.handle || p.url);
      for (var k = 0; k < all.length; k++) {
        if (all[k].length === 1) { if (s.indexOf(all[k]) === -1) return false; }
        else if (h.indexOf(all[k]) === -1) return false;
      }
      return true;
    });
    if (hit) return hit;

    /* P2 - a model code, letters and digits together: XC40, EV3, ID7, 500e. */
    if (codes.length) {
      hit = scan(function (p, h) { return contains(h, codes); });
      if (hit) return hit;
    }

    /* P3 - a bare number, as a whole handle segment. */
    if (nums.length) {
      hit = scan(function (p) {
        var s = segs(p.handle || p.url);
        for (var k = 0; k < nums.length; k++) if (s.indexOf(nums[k]) > -1) return true;
        return false;
      });
      if (hit) return hit;
    }

    /* P4 - a model word, on a page naming no number of its own.
     *
     * "EQA 250" must reach the EQA page even though 250 appears nowhere on it,
     * because 250 is a trim. "IONIQ 9" must NOT reach the unnumbered Ioniq
     * page, because that is the 2016 car. What separates them is the rest of
     * the catalogue: Hyundai sells an Ioniq 5 and an Ioniq 6, so after "Ioniq"
     * the number identifies the car. Mercedes has no numbered EQA, so there
     * the number is decoration. If a numbered sibling exists, the number is
     * load-bearing and an unnumbered page will not do. */
    if (words.length) {
      hit = scan(function (p, h) {
        var hitWords = [], k;
        for (k = 0; k < words.length; k++) if (h.indexOf(words[k]) > -1) hitWords.push(words[k]);
        if (!hitWords.length) return false;

        var own = segs(p.handle || p.url).filter(isDigits);
        for (k = 0; k < own.length; k++) if (nums.indexOf(own[k]) === -1) return false;

        if (nums.length && !own.length) {
          for (k = 0; k < hitWords.length; k++) {
            for (var j = 0; j < products.length; j++) {
              var hj = usable(products[j]);
              if (hj === null) continue;
              if (hj.indexOf(hitWords[k]) > -1 && segs(products[j].handle || products[j].url).filter(isDigits).length) {
                return false;
              }
            }
          }
        }
        return true;
      });
      if (hit) return hit;
    }

    return null;
  }

  function promote(region, actions, product) {
    if (actions.querySelector('a[href="' + product.url + '"]')) return;

    /* One primary button only - demote whatever the worker made primary. */
    Array.prototype.forEach.call(actions.querySelectorAll("a.btn-dark"), function (a) {
      a.className = a.className.replace(/\bbtn-dark\b/, "").replace(/\s+/g, " ").trim();
    });

    var a = document.createElement("a");
    a.className = "btn btn-dark";
    a.href = product.url || ("/products/" + product.handle);
    a.textContent = product.title;
    actions.insertBefore(a, actions.firstChild);

    /* reg-lookup.js decides the state from whether a product link was present
     * when it rendered. It was not, so correct it. */
    region.className = region.className.replace(/\breg-result-fallback\b/, "reg-result-match");
    var head = region.querySelector(".reg-result-head");
    if (head) {
      for (var i = 0; i < head.childNodes.length; i++) {
        var n = head.childNodes[i];
        if (n.nodeType === 3 && n.nodeValue && n.nodeValue.trim()) {
          n.nodeValue = "Exact match";
          break;
        }
      }
    }
  }

  function apply(region) {
    if (region.hidden) return;
    var actions = region.querySelector(".reg-result-actions");
    if (!actions) return;
    /* Already a product link - the worker knew this one, or we have been here
     * already. Either way there is nothing to upgrade. */
    if (actions.querySelector('a[href*="/products/"]')) return;

    var car = carFrom(region);
    if (!car) return;
    if (region.getAttribute("data-product-match") === car.query) return;
    region.setAttribute("data-product-match", car.query);

    function finish(hit) {
      if (!hit) return false;
      /* Re-read: the customer may have switched mode while this was in
       * flight, and the V2L and portable results are correct as they stand. */
      var live = region.querySelector(".reg-result-actions");
      if (!live || live.querySelector('a[href*="/products/"]')) return true;
      var still = carFrom(region);
      if (!still || still.query !== car.query) return true;
      promote(region, live, hit);
      return true;
    }

    fromCollections(brandCollections(actions, car))
      .then(function (products) {
        if (finish(pick(products, car))) return null;
        return fromSearch(car.query).then(function (ps) { finish(pick(ps, car)); });
      })
      .catch(function () { /* leave the worker's collection links alone */ });
  }

  function watch() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-reg-result]"), function (r) {
      if (r.getAttribute("data-product-watch")) return;
      r.setAttribute("data-product-watch", "1");
      apply(r);
      if (window.MutationObserver) {
        new MutationObserver(function () { apply(r); }).observe(r, { childList: true, subtree: true });
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watch);
  } else {
    watch();
  }
  document.addEventListener("shopify:section:load", watch);
})();
